// AgreementsInfoView.tsx
import React from 'react';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';
import Link from '@/presentation/reusables/Link';
import { ContainerProcedure } from '../../../certificates-constancies/type1/info/components';
import { BackButton } from '@/presentation/modules/procedures/components';
import Spinner from '@/presentation/components/Spinner';
import { Block, Heading, Select, Text } from '@/presentation/components';
import ProcedureHeader from '@/presentation/modules/procedures/components/ContainerProcedure/Header';
import CheckedListItems from '@/presentation/reusables/CheckedList';
import { SaveButton } from '../../bse/components/styled';
import RequestExist from '@/presentation/modules/procedures/components/Modals/RequestExists';
import NotAvailable from '@/presentation/modules/procedures/components/Modals/NotAvailable';
import Modal from '../../../../components/Modal/Modal';
import Processing from '@/presentation/components/Processing';

export interface AgreementsInfoViewProps {
  title: string;
  otherDni: string;
  onOtherDniChange: (value: string) => void;
  onLoadOtherDni: () => void;
  costLabel: string;
  daysLabel: string;
  deadlineLabel: string;
  beneficiaryTypes: { id: string; name: string }[];
  selectedBeneficiary: string;
  onBeneficiaryChange: (value: string) => void;
  discountText: string;
  requirementsAuto: { text: string; valid: boolean | null }[];
  otherRequirements: { text: string; linkText: string; linkUrl: string }[];
  documents: string[];
  onStartRequest: () => void;
  handleBack: () => void;
  isLoading: boolean;
  showModalNotAvailable: boolean;
  showModalExistRequest: boolean;
  showModalApproved: boolean;
  handleCloseModalNotAvailable: () => void;
  handleButtonModalNotAvailable: () => void;
  handleCloseModalExistRequest: () => void;
  handleButtonModalExistRequest: () => void;
  handleCloseModalApproved: () => void;
  handleButtonModalApproved: () => void;
  isLoadingValidations: boolean;
}

const InputGroup = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: ${({ gap }) => gap ?? '4px'};
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0px 1.2px 4px 0px #eaeef1;
  @media (min-width: 640px) {
    flex: 1;
  }
`;
const Label = styled.label`
  font-size: 14px;
  font-weight: 400;
  color: #161d1f;
`;
const ValueBox = styled.div`
  padding: 16px;
  gap: 4px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0px 1.2px 4px 0px #eaeef1;
  @media (min-width: 640px) {
    height: 100%;
    border-radius: 8px;
    align-content: center;
    flex: 1;
  }
`;
const ValueLabel = styled.div`
  font-family: Lato;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  color: #161d1f;
`;
const ValueText = styled.div`
  font-family: Lato;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: #161d1f;
`;
const TextInput = styled.input`
  padding: 10px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  height: 40px;
  @media (min-width: 640px) {
    width: 471px;
    height: 40px;
    margin-right: 8px;
  }
`;
const DniButton = styled(SaveButton)`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 120px;
  height: 44px;
  border-radius: 4px;
  font-family: Lato;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;

  @media (max-width: 639px) {
    width: 100%;
  }
`;
interface HighlightBoxProps {
  textColor?: string;
  bgColor?: string;
}
const HighlightBox = styled.div<HighlightBoxProps>`
  width: 100%;
  padding: 8px;
  gap: 8px;
  border-radius: 8px;
  background: ${({ bgColor }) => bgColor ?? '#A8F1BB'};
  font-family: Lato;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  color: ${({ textColor }) => textColor ?? '#003F18'};
`;
const ResponsiveFlex = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 16px;
  width: 100%;
  @media (min-width: 640px) {
    flex-direction: row;
    height: 96px;
    gap: 24px;
  }
`;
const ResponsiveFlex2 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  @media (min-width: 768px) {
    width: 639px;
    margin: 0 auto;
  }
`;

// DniSection component
const DniSection: React.FC<
  Pick<
    AgreementsInfoViewProps,
    | 'selectedBeneficiary'
    | 'beneficiaryTypes'
    | 'otherDni'
    | 'onOtherDniChange'
    | 'onLoadOtherDni'
  >
> = ({
  selectedBeneficiary,
  beneficiaryTypes,
  otherDni,
  onOtherDniChange,
  onLoadOtherDni
}) => {
  if (!selectedBeneficiary || selectedBeneficiary === beneficiaryTypes[0]?.id)
    return null;

  let labelText = 'Número de DNI';
  if (selectedBeneficiary === beneficiaryTypes[1]?.id) {
    labelText = 'Número de DNI de tu cónyuge';
  } else if (selectedBeneficiary === beneficiaryTypes[2]?.id) {
    labelText = 'Número de DNI de tu padre o madre';
  }

  const isValidDni = /^\d{8}$/.test(otherDni);

  return (
    <ResponsiveFlex2>
      <InputGroup gap="0px">
        <Block customized padding="0px 0px 12px 0px">
          <Heading
            text="Ingresa el DNI del colaborador para validar automáticamente si puedes acceder al beneficio."
            level={3}
            color="var(--text-text02)"
            size={14}
            lineHeight={20}
            fontWeight="700"
            align="flex-start"
            width="100%"
          />
        </Block>
        <Label style={{ marginBottom: '4px' }}>{labelText}</Label>
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '8px',
            alignItems: 'center'
          }}
        >
          <TextInput
            type="text"
            value={otherDni}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, '');
              onOtherDniChange(onlyDigits.slice(0, 8));
            }}
            inputMode="numeric"
            maxLength={8}
            placeholder="Ingrese DNI"
          />
          <DniButton onClick={onLoadOtherDni} disabled={!isValidDni}>
            Validar DNI
          </DniButton>
        </div>
      </InputGroup>
    </ResponsiveFlex2>
  );
};

// RequirementsSection component
const RequirementsSection: React.FC<
  Pick<AgreementsInfoViewProps, 'requirementsAuto' | 'otherRequirements'>
> = ({ requirementsAuto, otherRequirements }) => (
  <Block customized gap="1px" backgroundColor="#D5E5F0" borderRadius="8px">
    {requirementsAuto.length > 0 && (
      <Block
        customized
        maxWidth={isMobile ? '100%' : '744px'}
        backgroundColor="#fff"
        padding="16px"
        borderRadius="8px 8px 0 0"
      >
        <Heading
          text="Validación automática de requisitos:"
          level={3}
          color="var(--text-text02)"
          size={14}
          lineHeight={20}
          fontWeight="700"
          align="flex-start"
          width="100%"
        />
        <Block customized padding="8px 0px 0px 0px">
          <CheckedListItems
            paddingBottom="8px"
            items={requirementsAuto.map((req) => ({
              itemText: req.text,
              isChecked: req.valid === true,
              isDisabled: req.valid === null,
              separator: requirementsAuto[requirementsAuto.length - 1] !== req
            }))}
          />
          {requirementsAuto.every((req) => req.valid === true) ? (
            <HighlightBox bgColor="#A8F1BB" textColor="#003F18">
              ¡Sí cumples con los requisitos mínimos para este convenio!
            </HighlightBox>
          ) : (
            <HighlightBox bgColor="#FDADB3" textColor="#890722">
              No cumples con todos los requisitos. Podrás intentarlo en el
              próximo periodo.
            </HighlightBox>
          )}
        </Block>
      </Block>
    )}
    {otherRequirements.length > 0 && (
      <Block
        customized
        maxWidth={isMobile ? '100%' : '744px'}
        backgroundColor="#fff"
        padding="16px"
        borderRadius={requirementsAuto.length > 0 ? '0 0 8px 8px' : '8px'}
      >
        <Heading
          text={
            requirementsAuto.length > 0
              ? 'Otros requisitos que necesitas:'
              : 'Requisitos:'
          }
          level={3}
          color="var(--text-text02)"
          size={14}
          lineHeight={20}
          fontWeight="700"
          align="flex-start"
          width="100%"
        />
        <Block customized padding="4px 0px 0px 16px">
          <ul style={{ listStyleType: 'disc', color: '#161D1F' }}>
            {otherRequirements.map((o) => (
              <li key={o.text}>
                {o.text}{' '}
                <Link
                  linkName={o.linkText}
                  linkType="external"
                  url={o.linkUrl}
                  iconName={o.linkUrl ? 'external-link' : undefined}
                />
              </li>
            ))}
          </ul>
        </Block>
      </Block>
    )}
  </Block>
);

// Component for documents section
const DocumentsSection: React.FC<{
  selectedBeneficiary: string;
  documents: string[];
}> = ({ selectedBeneficiary, documents }) => (
  <Block
    customized
    maxWidth={isMobile ? '100%' : '744px'}
    backgroundColor="#fff"
    padding="16px"
    borderRadius="8px"
  >
    <Heading
      text="Documentos de sustento:"
      level={3}
      color={selectedBeneficiary ? '#161D1F' : '#4F6168'}
      size={14}
      lineHeight={20}
      fontWeight="700"
      align="flex-start"
      width="100%"
    />
    {!selectedBeneficiary ? (
      <Block padding="8px 0px">
        <Text color={'#4F6168'} size={14}>
          Debes elegir qué tipo de beneficiario eres para visualizar los
          documentos de sustento.
        </Text>
      </Block>
    ) : (
      <Block customized padding="4px 0px 0px 16px">
        <ul style={{ listStyleType: 'disc', color: '#161D1F' }}>
          {documents.map((doc) => (
            <li key={doc}>{doc}</li>
          ))}
        </ul>
      </Block>
    )}
  </Block>
);

// Component for steps section
const StepsSection: React.FC = () => (
  <Block
    customized
    maxWidth={isMobile ? '100%' : '744px'}
    backgroundColor="#fff"
    padding="16px"
    borderRadius="8px"
    gap="8px"
    lineHeight="20px"
  >
    <Heading
      text="Pasos a seguir:"
      level={3}
      color="var(--text-text02)"
      size={14}
      lineHeight={20}
      fontWeight="700"
      align="flex-start"
      width="100%"
    />
    <Block customized padding="4px 0px 0px 16px">
      <ol style={{ listStyle: 'decimal', color: '#161D1F' }}>
        <li>
          Selecciona el tipo de beneficiario. Luego, adjunta los documentos de
          sustento o confirma el envío de tu solicitud.
        </li>
        <li>
          Recibe la conformidad u observaciones, una vez finalizado en el
          trámite.
        </li>
      </ol>
    </Block>
  </Block>
);

// Component for modals
const ModalsSection: React.FC<
  Pick<
    AgreementsInfoViewProps,
    | 'showModalNotAvailable'
    | 'showModalExistRequest'
    | 'showModalApproved'
    | 'handleCloseModalNotAvailable'
    | 'handleButtonModalNotAvailable'
    | 'handleCloseModalExistRequest'
    | 'handleButtonModalExistRequest'
    | 'handleCloseModalApproved'
    | 'handleButtonModalApproved'
  >
> = ({
  showModalNotAvailable,
  handleCloseModalNotAvailable,
  handleButtonModalNotAvailable,
  showModalExistRequest,
  handleCloseModalExistRequest,
  handleButtonModalExistRequest,
  showModalApproved,
  handleCloseModalApproved,
  handleButtonModalApproved
}) => (
  <>
    <Modal
      show={showModalNotAvailable}
      handleClose={handleCloseModalNotAvailable}
    >
      <NotAvailable
        handleClick={handleButtonModalNotAvailable}
        textInformation="Esta solicitud no se encuentra disponible"
      />
    </Modal>
    <Modal
      show={showModalExistRequest}
      handleClose={handleCloseModalExistRequest}
    >
      <RequestExist
        handleClick={handleButtonModalExistRequest}
        textInformation="Actualmente tienes una solicitud en proceso"
      />
    </Modal>
    <Modal show={showModalApproved} handleClose={handleCloseModalApproved}>
      <RequestExist
        handleClick={handleButtonModalApproved}
        textInformation="Tu solicitud ha sido aprobada"
      />
    </Modal>
  </>
);

const AgreementsInfoView: React.FC<AgreementsInfoViewProps> = ({
  title,
  otherDni,
  onOtherDniChange,
  onLoadOtherDni,
  costLabel,
  daysLabel,
  deadlineLabel,
  beneficiaryTypes,
  selectedBeneficiary,
  onBeneficiaryChange,
  discountText,
  requirementsAuto,
  otherRequirements,
  documents,
  onStartRequest,
  handleBack,
  isLoading,
  showModalNotAvailable,
  showModalExistRequest,
  showModalApproved,
  handleCloseModalNotAvailable,
  handleButtonModalNotAvailable,
  handleCloseModalExistRequest,
  handleButtonModalExistRequest,
  handleCloseModalApproved,
  handleButtonModalApproved,
  isLoadingValidations
}) => {
  const isValidDni = /^\d{8}$/.test(otherDni);
  return (
    <ContainerProcedure>
      <Processing isOpen={isLoadingValidations} />
      <SubContainer style={{ marginBottom: 12 }}>
        <Block
          customized
          width="100%"
          display="flex"
          justifyContent="flex-start"
          padding="16px 0 0 0"
        >
          <BackButton onClick={handleBack} fontSize="14px" />
        </Block>
      </SubContainer>
      <SubContainer>
        <Block customized padding=" 0 0 16px 0">
          <ProcedureHeader name={title} />
        </Block>
      </SubContainer>
      {isLoading ? (
        <Spinner />
      ) : (
        <SubContainer>
          <ProcedureHeader
            amount={costLabel}
            duration={daysLabel}
            date={deadlineLabel}
          />
          <ResponsiveFlex>
            <InputGroup>
              <Label>¿Qué tipo de beneficiario eres?</Label>
              <Select
                options={beneficiaryTypes.map((b) => ({
                  label: b.name,
                  value: b.id
                }))}
                placeholder="Selecciona un beneficiario"
                handleChangeSelect={(option) =>
                  onBeneficiaryChange(option.value)
                }
              />
            </InputGroup>
            <ValueBox>
              <ValueLabel>Porcentaje de descuento</ValueLabel>
              <ValueText>{discountText}</ValueText>
            </ValueBox>
          </ResponsiveFlex>{' '}
          <DniSection
            selectedBeneficiary={selectedBeneficiary}
            beneficiaryTypes={beneficiaryTypes}
            otherDni={otherDni}
            onOtherDniChange={onOtherDniChange}
            onLoadOtherDni={onLoadOtherDni}
          />
          <RequirementsSection
            requirementsAuto={requirementsAuto}
            otherRequirements={otherRequirements}
          />
          <DocumentsSection
            selectedBeneficiary={selectedBeneficiary}
            documents={documents}
          />
          <StepsSection />
          <Block customized>
            <SaveButton
              onClick={onStartRequest}
              disabled={
                !(
                  requirementsAuto.length > 0 &&
                  requirementsAuto.every((req) => req.valid === true)
                ) ||
                (selectedBeneficiary === beneficiaryTypes[1]?.id ||
                selectedBeneficiary === beneficiaryTypes[2]?.id
                  ? !isValidDni
                  : false)
              }
            >
              Empezar solicitud
            </SaveButton>
          </Block>
        </SubContainer>
      )}
      <ModalsSection
        showModalNotAvailable={showModalNotAvailable}
        handleCloseModalNotAvailable={handleCloseModalNotAvailable}
        handleButtonModalNotAvailable={handleButtonModalNotAvailable}
        showModalExistRequest={showModalExistRequest}
        handleCloseModalExistRequest={handleCloseModalExistRequest}
        handleButtonModalExistRequest={handleButtonModalExistRequest}
        showModalApproved={showModalApproved}
        handleCloseModalApproved={handleCloseModalApproved}
        handleButtonModalApproved={handleButtonModalApproved}
      />
    </ContainerProcedure>
  );
};

export default AgreementsInfoView;
